import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Shield, Save, CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import API_URL from '../lib/apiUrl';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';


const AssessmentPage = () => {
  const { assessmentId } = useParams();
  const [controls, setControls] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { token } = useAuth();

  const fetchControls = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/controls`);
      setControls(response.data.all_controls);
    } catch (error) {
      toast.error('Failed to load controls');
    }
  }, []);

  const fetchAssessment = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/assessments/${assessmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const answersMap = {};
      response.data.answers.forEach(answer => {
        answersMap[answer.control_id] = {
          rating: answer.rating,
          notes: answer.notes || ''
        };
      });
      setAnswers(answersMap);
    } catch (error) {
      toast.error('Failed to load assessment');
    } finally {
      setLoading(false);
    }
  }, [assessmentId, token]);

  useEffect(() => {
    fetchControls();
    fetchAssessment();
  }, [fetchControls, fetchAssessment]);

  const handleRating = (controlId, rating) => {
    setAnswers({
      ...answers,
      [controlId]: {
        rating,
        notes: answers[controlId]?.notes || ''
      }
    });
  };

  const handleNotes = (controlId, notes) => {
    setAnswers({
      ...answers,
      [controlId]: {
        rating: answers[controlId]?.rating || 0,
        notes
      }
    });
  };

  const handleSave = async () => {
    const answersArray = Object.entries(answers)
      .filter(([_, value]) => value.rating > 0)
      .map(([controlId, value]) => {
        const control = controls.find(c => c.id === controlId);
        return {
          control_id: controlId,
          category: control?.category || '',
          rating: value.rating,
          notes: value.notes
        };
      });

    const progress = (answersArray.length / controls.length) * 100;

    try {
      await axios.put(
        `${API_URL}/api/assessments/${assessmentId}`,
        {
          answers: answersArray,
          progress,
          status: 'in_progress'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Progress saved!');
    } catch (error) {
      toast.error('Failed to save progress');
    }
  };

  const handleComplete = async () => {
    const answersArray = Object.entries(answers)
      .filter(([_, value]) => value.rating > 0)
      .map(([controlId, value]) => {
        const control = controls.find(c => c.id === controlId);
        return {
          control_id: controlId,
          category: control?.category || '',
          rating: value.rating,
          notes: value.notes
        };
      });

    if (answersArray.length < controls.length) {
      toast.error('Please answer all questions before completing');
      return;
    }

    try {
      await axios.put(
        `${API_URL}/api/assessments/${assessmentId}`,
        {
          answers: answersArray,
          progress: 100,
          status: 'completed'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Assessment completed!');
      navigate(`/results/${assessmentId}`);
    } catch (error) {
      toast.error('Failed to complete assessment');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const currentControl = controls[currentIndex];
  const progress = (Object.keys(answers).filter(key => answers[key]?.rating > 0).length / controls.length) * 100;

  return (
    <div className="bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <Shield className="h-8 w-8 text-slate-900" />
              <span className="font-heading font-bold text-xl text-slate-900">ISO 27001:2022 Assessment</span>
            </div>
            <div className="flex items-center space-x-3">
              <Button onClick={handleSave} variant="outline" data-testid="save-progress-button">
                <Save className="mr-2 h-4 w-4" />
                Save Progress
              </Button>
              <Button onClick={() => navigate('/dashboard')} variant="ghost">Exit</Button>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono text-xs uppercase tracking-widest text-slate-500">
                Question {currentIndex + 1} of {controls.length}
              </span>
              <span className="font-mono text-xs text-slate-500">{Math.round(progress)}% Complete</span>
            </div>
            <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-slate-900 progress-bar"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Assessment Content */}
      <div className="max-w-5xl mx-auto px-6 py-12">
        <AnimatePresence mode="wait">
          {currentControl && (
            <motion.div
              key={currentControl.id}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-lg shadow-lg p-8 border border-slate-200"
            >
              {/* Category Badge */}
              <div className="inline-block mb-4">
                <span className="font-mono text-xs uppercase tracking-widest text-blue-600 bg-blue-50 px-4 py-2 rounded-full border border-blue-200">
                  {currentControl.category} Controls
                </span>
              </div>

              {/* Control ID */}
              <div className="font-heading font-bold text-sm text-slate-500 mb-2">
                {currentControl.id}
              </div>

              {/* Control Title */}
              <h2 className="font-heading font-bold text-3xl text-slate-900 mb-4">
                {currentControl.title}
              </h2>

              {/* Control Description */}
              <p className="font-body text-lg text-slate-600 leading-relaxed mb-8">
                {currentControl.description}
              </p>

              {/* Rating */}
              <div className="mb-6">
                <Label className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-3 block">
                  Maturity Rating (1 = Initial, 5 = Optimized)
                </Label>
                <div className="flex space-x-4">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      onClick={() => handleRating(currentControl.id, rating)}
                      className={`flex-1 py-4 px-6 rounded-lg border-2 font-heading font-semibold text-lg transition-all ${
                        answers[currentControl.id]?.rating === rating
                          ? 'border-slate-900 bg-slate-900 text-white'
                          : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
                      }`}
                      data-testid={`rating-${rating}`}
                    >
                      {rating}
                    </button>
                  ))}
                </div>
                <div className="flex justify-between mt-2 px-2">
                  <span className="font-body text-xs text-slate-500">Initial</span>
                  <span className="font-body text-xs text-slate-500">Optimized</span>
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-2 block">
                  Notes (Optional)
                </Label>
                <Textarea
                  value={answers[currentControl.id]?.notes || ''}
                  onChange={(e) => handleNotes(currentControl.id, e.target.value)}
                  placeholder="Add any comments or observations..."
                  rows={3}
                  className="font-body"
                  data-testid="notes-textarea"
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8">
          <Button
            onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
            disabled={currentIndex === 0}
            variant="outline"
            size="lg"
            data-testid="previous-button"
          >
            <ChevronLeft className="mr-2 h-5 w-5" />
            Previous
          </Button>

          {currentIndex === controls.length - 1 ? (
            <Button
              onClick={handleComplete}
              size="lg"
              className="font-semibold"
              data-testid="complete-button"
            >
              <CheckCircle className="mr-2 h-5 w-5" />
              Complete Assessment
            </Button>
          ) : (
            <Button
              onClick={() => setCurrentIndex(Math.min(controls.length - 1, currentIndex + 1))}
              size="lg"
              data-testid="next-button"
            >
              Next
              <ChevronRight className="ml-2 h-5 w-5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssessmentPage;